/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, PublicOnly, AdminRoute, ProfessionalOnly } from '@/routes/guards';

import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import PatientRegisterPage from '@/pages/PatientRegisterPage';
import WelcomePage from '@/pages/WelcomePage';
import FeedPage from '@/pages/FeedPage';
import PostDetailPage from '@/pages/PostDetailPage';
import BachecaPage from '@/pages/BachecaPage';
import ResourcesPage from '@/pages/ResourcesPage';
import ChatListPage from '@/pages/ChatListPage';
import ChatConversationPage from '@/pages/ChatConversationPage';
import DirectoryPage from '@/pages/DirectoryPage';
import ProfilePage from '@/pages/ProfilePage';
import SettingsPage from '@/pages/SettingsPage';
import AdminPage from '@/pages/AdminPage';
import WellnessPage from '@/pages/WellnessPage';
import AIChatPage from '@/pages/AIChatPage';
import FeedbackPage from '@/pages/FeedbackPage';
import SavedPage from '@/pages/SavedPage';
import ShiftsPage from '@/pages/ShiftsPage';
import InvoicingPage from '@/pages/InvoicingPage';
import KioskPage from '@/pages/KioskPage';
import BrandSettingsPage from '@/pages/admin/BrandSettingsPage';
import StripeConfigPage from '@/pages/admin/StripeConfigPage';
import EmailSettingsPage from '@/pages/admin/EmailSettingsPage';
import TelemedicinePage from '@/pages/TelemedicinePage';
import TelemedicinePaymentResultPage from '@/pages/TelemedicinePaymentResultPage';
import VideoRoomPage from '@/pages/telemedicine/VideoRoomPage';
import PatientAIPage from '@/pages/PatientAIPage';
import SecurityPage from '@/pages/SecurityPage';
import WalletPage from '@/pages/WalletPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import MagicLinkRequestPage from '@/pages/MagicLinkRequestPage';
import MagicLinkCallbackPage from '@/pages/MagicLinkCallbackPage';
import SymptomCheckerPage from '@/pages/SymptomCheckerPage';
import DrugCheckerPage from '@/pages/DrugCheckerPage';
import DoctorInsightsPage from '@/pages/DoctorInsightsPage';
import MyReviewsPage from '@/pages/MyReviewsPage';
import SecondOpinionPage from '@/pages/SecondOpinionPage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/legal/CookiePolicyPage';
import DPAPage from '@/pages/legal/DPAPage';
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage';
import LicensePage from '@/pages/legal/LicensePage';
import OnboardingPage from '@/pages/OnboardingPage';
import HealthDataPage from '@/pages/HealthDataPage';
import ProfessionalServicesPage from '@/pages/ProfessionalServicesPage';
import WoundCareListPage, { WoundCaseDetailPage } from '@/pages/WoundCarePage';
import CareTeamPage from '@/pages/CareTeamPage';
import IdentityVerificationPage from '@/pages/IdentityVerificationPage';
import FhirPage from '@/pages/FhirPage';
import GdprSettingsPage from '@/pages/GdprSettingsPage';
import DoctorAnalyticsPage from '@/pages/DoctorAnalyticsPage';
import PublicDoctorProfilePage from '@/pages/PublicDoctorProfilePage';
import ProToolkitPage from '@/pages/ProToolkitPage';
import AgenticHealthPage from '@/pages/AgenticHealthPage';
import VoiceDiaryPage from '@/pages/VoiceDiaryPage';
import FSELombardiaPage from '@/pages/FSELombardiaPage';
import EcmPage from '@/pages/EcmPage';
import ConsentPage from '@/pages/ConsentPage';
import DirezionePage from '@/pages/admin/DirezionePage';
import BackupPage from '@/pages/admin/BackupPage';
import SetupWizardPage from '@/pages/SetupWizardPage';
import PatientPortalPage from '@/pages/PatientPortalPage';
import SendReportPage from '@/pages/SendReportPage';
import ValidationPage from '@/pages/admin/ValidationPage';
import AiUsagePage from '@/pages/admin/AiUsagePage';
import AppShell from '@/components/AppShell';

/**
 * AppRoutes — the complete route table of the application.
 * Extracted from App.js (Code Quality refactor): App.js only wires providers,
 * this file owns the URL → page mapping and the access guards.
 */
export const AppRoutes = () => (
  <Routes>
    <Route path="/welcome" element={<PublicOnly><WelcomePage /></PublicOnly>} />
    <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
    <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
    <Route path="/reset-password" element={<PublicOnly><ResetPasswordPage /></PublicOnly>} />
    <Route path="/auth/magic-link" element={<PublicOnly><MagicLinkRequestPage /></PublicOnly>} />
    <Route path="/auth/magic" element={<MagicLinkCallbackPage />} />
    <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
    <Route path="/register/patient" element={<PublicOnly><PatientRegisterPage /></PublicOnly>} />

    {/* Installer Wizard white-label (gate con password di setup, nessuna sessione richiesta) */}
    <Route path="/setup" element={<SetupWizardPage />} />

    {/* Full-screen protected routes (no AppShell) */}
    <Route path="/telemedicine/room/:appt_id" element={<ProtectedRoute><VideoRoomPage /></ProtectedRoute>} />

    {/* Legal pages - public accessible (no auth required) */}
    <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
    <Route path="/legal/cookie" element={<CookiePolicyPage />} />
    <Route path="/legal/dpa" element={<DPAPage />} />
    <Route path="/legal/terms" element={<TermsOfServicePage />} />
    <Route path="/legal/license" element={<LicensePage />} />
    <Route path="/legal" element={<Navigate to="/legal/privacy" replace />} />

    {/* Public doctor profile (SEO) */}
    <Route path="/dr/:slug" element={<PublicDoctorProfilePage />} />

    <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
      <Route path="/" element={<Navigate to="/feed" replace />} />
      <Route path="/feed" element={<ProfessionalOnly><FeedPage /></ProfessionalOnly>} />
      <Route path="/posts/:id" element={<ProfessionalOnly><PostDetailPage /></ProfessionalOnly>} />
      <Route path="/bacheca" element={<ProfessionalOnly><BachecaPage /></ProfessionalOnly>} />
      <Route path="/initiatives" element={<Navigate to="/bacheca" replace />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/wellness" element={<ProfessionalOnly><WellnessPage /></ProfessionalOnly>} />
      <Route path="/ai" element={<ProfessionalOnly><AIChatPage /></ProfessionalOnly>} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/saved" element={<ProfessionalOnly><SavedPage /></ProfessionalOnly>} />
      <Route path="/shifts" element={<ProfessionalOnly><ShiftsPage /></ProfessionalOnly>} />
      <Route path="/invoicing" element={<ProfessionalOnly><InvoicingPage /></ProfessionalOnly>} />
      <Route path="/kiosk" element={<ProfessionalOnly><KioskPage /></ProfessionalOnly>} />
      <Route path="/admin/brand-settings" element={<AdminRoute><BrandSettingsPage /></AdminRoute>} />
      <Route path="/admin/stripe-config" element={<AdminRoute><StripeConfigPage /></AdminRoute>} />
      <Route path="/admin/email" element={<AdminRoute><EmailSettingsPage /></AdminRoute>} />
      <Route path="/admin/direzione" element={<AdminRoute><DirezionePage /></AdminRoute>} />
      <Route path="/admin/backup" element={<AdminRoute><BackupPage /></AdminRoute>} />
      <Route path="/admin/validation" element={<AdminRoute><ValidationPage /></AdminRoute>} />
      <Route path="/admin/ai-usage" element={<AdminRoute><AiUsagePage /></AdminRoute>} />
      <Route path="/consents" element={<ProfessionalOnly><ConsentPage /></ProfessionalOnly>} />
      <Route path="/referti" element={<ProfessionalOnly><SendReportPage /></ProfessionalOnly>} />
      <Route path="/portale" element={<PatientPortalPage />} />
      <Route path="/telemedicine" element={<TelemedicinePage />} />
      <Route path="/telemedicine/subscription/success" element={<TelemedicinePaymentResultPage context="subscription" outcome="success" />} />
      <Route path="/telemedicine/subscription/cancel" element={<TelemedicinePaymentResultPage context="subscription" outcome="cancel" />} />
      <Route path="/telemedicine/appointments/success" element={<TelemedicinePaymentResultPage context="appointment" outcome="success" />} />
      <Route path="/telemedicine/appointments/cancel" element={<TelemedicinePaymentResultPage context="appointment" outcome="cancel" />} />
      <Route path="/telemedicine/premium/success" element={<TelemedicinePaymentResultPage context="premium" outcome="success" />} />
      <Route path="/telemedicine/premium/cancel" element={<TelemedicinePaymentResultPage context="premium" outcome="cancel" />} />
      <Route path="/ai-patient" element={<PatientAIPage />} />
      <Route path="/chat" element={<ChatListPage />} />
      <Route path="/chat/:id" element={<ChatConversationPage />} />
      <Route path="/directory" element={<DirectoryPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/profile/:id" element={<ProfilePage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/wallet" element={<WalletPage />} />
      <Route path="/ai/symptoms" element={<SymptomCheckerPage />} />
      <Route path="/ai/drugs" element={<DrugCheckerPage />} />
      <Route path="/ai/insights" element={<DoctorInsightsPage />} />
      <Route path="/my-reviews" element={<MyReviewsPage />} />
      <Route path="/second-opinion" element={<SecondOpinionPage />} />
      <Route path="/health-data" element={<HealthDataPage />} />
      <Route path="/my-services" element={<ProfessionalServicesPage />} />
      <Route path="/wound-care" element={<WoundCareListPage />} />
      <Route path="/wound-care/:caseId" element={<WoundCaseDetailPage />} />
      <Route path="/care-team" element={<CareTeamPage />} />
      <Route path="/identity" element={<IdentityVerificationPage />} />
      <Route path="/fhir" element={<FhirPage />} />
      <Route path="/settings/gdpr" element={<GdprSettingsPage />} />
      <Route path="/analytics" element={<DoctorAnalyticsPage />} />
      <Route path="/pro-toolkit" element={<ProToolkitPage />} />
      <Route path="/agentic-health" element={<AgenticHealthPage />} />
      <Route path="/voice-diary" element={<ProfessionalOnly><VoiceDiaryPage /></ProfessionalOnly>} />
      <Route path="/fse" element={<FSELombardiaPage />} />
      <Route path="/ecm" element={<ProfessionalOnly><EcmPage /></ProfessionalOnly>} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
    </Route>

    <Route path="*" element={<Navigate to="/welcome" replace />} />
  </Routes>
);

export default AppRoutes;
